import { ToastContext } from "context/ToastContext";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import type {
  IRuleGroupUpdateRequest,
  IRuleGroupUpdateResponse,
} from "types/AdjustmentRules/AdjustmentRules";

import { omitScopeFields } from "../helpers/mapFormToRequestBody";
import {
  buildRuleGroupUpdateRequest,
  extractServerErrorMessage,
  payerCountLabel,
} from "../helpers/ruleBucket";
import { useRuleBucket } from "./useRuleBucket";

type TGroupUpdateTrigger = (request: IRuleGroupUpdateRequest<any>) => {
  unwrap: () => Promise<IRuleGroupUpdateResponse<unknown>>;
};

/**
 * CLE-2842 — saves a bucket-mode edit through the type's `…Group` mutation: one atomic request
 * that updates every member, deletes the removed ones and copies the rule to the added payers.
 *
 * Returns null when the page is not in bucket mode with "Apply changes to all payers" on — the
 * caller then saves the single row exactly as before.
 */
export function useSubmitRuleGroup(trigger: TGroupUpdateTrigger) {
  const bucket = useRuleBucket();
  const navigate = useNavigate();
  const { current: toastElement } = useContext(ToastContext);

  if (!bucket?.applyToAll) return null;

  return async (singleRowBody: Record<string, unknown>) => {
    const request = buildRuleGroupUpdateRequest({
      members: bucket.members,
      groupKey: bucket.groupKey,
      data: omitScopeFields(singleRowBody),
      removedIds: bucket.removedIds,
      addedPayerIds: bucket.addedPayers.map((payer) => payer.value),
    });
    try {
      const response = await trigger(request).unwrap();
      const savedCount = Array.isArray(response?.updated)
        ? response.updated.length + (response.created?.length ?? 0)
        : bucket.activePayers.length;
      toastElement?.show({
        severity: "success",
        summary: `Rule updated for ${payerCountLabel(savedCount)}`,
      });
      // Never back to this edit page — its row may have been one of the deleted members.
      navigate("/payer-rules");
    } catch (error) {
      // 409 means the bucket changed since it was loaded; the server's message says to reload,
      // and the form is left as it is so nothing the user typed is lost.
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail:
          extractServerErrorMessage(error) ??
          "An error has occurred attempting to update this rule for every payer",
        life: 8000,
      });
    }
  };
}
